import React from "react";

/**
 * `useState` that mirrors its value to `localStorage` so a view survives
 * navigating away and back (e.g. a curator opening a HITL item and returning
 * to the queue). Pass a `null` key to opt out of persistence entirely — the
 * hook then behaves like a plain `useState(initialValue)`.
 */
export function usePersistedState<T>(
  key: string | null,
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = React.useState<T>(() => {
    if (!key || typeof window === "undefined") return initialValue;
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initialValue : (JSON.parse(raw) as T);
    } catch {
      return initialValue;
    }
  });

  React.useEffect(() => {
    if (!key || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage full or unavailable — fall back to in-memory state */
    }
  }, [key, value]);

  return [value, setValue];
}
